/* eslint-disable import/no-unresolved */
import { Button } from "@mui/material";
import DetailsHeading from "src/shared/DetailsHeading";
import { useFormik } from "formik";
import { useAppSelector } from "src/utils/hooks/useAppSelector";
import { useDispatch } from "react-redux";
import {
  setStepDetails,
  setOtpVerificationDetails,
  SET_TOAST_ERROR
} from "src/redux/Actions/common";
import React, { useState } from "react";
import CustomModal from "src/shared/CustomModal";
import { validationSchema } from "src/helpers/FormDetails/AssignmentClause";
import { setAssignmentClause } from "src/redux/Actions/assignmentClause";
import { updateExistingAssignmentDetails } from "src/services/AssignmentClauseDetails";
import { useMutation } from "react-query";
import CustomBackDrop from "src/shared/CustomBackDrop";
import OtpService from "src/services/Otp";
import store from "src/redux/store";
import StorageService from "src/utils/Storage/storageService";
import { roleIDs } from "src/config/constant";
import AssignmentClause from "./AssignmentClause";
import { prepareAssignmentRequestBody } from "../../../helpers/FormDetails/AssignmentClause/resources";
import CustomOTPMobileModal from "../../../shared/CustomOTPMobileModal";

function AssignmentClauseDetails() {
  const dispatch = useDispatch();
  const parentStepNumber = useAppSelector((state) => state.stepDetails.parentStepNo);
  const assignmentClauseDetails = useAppSelector((state) => state.assignmentClauseDetails);
  const proposalStatus = useAppSelector((state) => state.proposalStatus.status);
  const leadId = useAppSelector((state) => state?.common?.leadId);
  const partnerData = useAppSelector((state) => state?.common?.partnerData);
  const [modalOpen, setModalOpen] = useState(false);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const { mutate: updateAssignmentDetails, isLoading: isAssignmentSaving } = useMutation(
    updateExistingAssignmentDetails
  );
  const mefSchema = useAppSelector((state): any => state.masterdata_reducer?.mefJourneySchema);
  const assignmentMefFields = mefSchema?.assignmentNotice || {};
  const formik = useFormik({
    enableReinitialize: true,
    validateOnMount: true,
    validateOnBlur: true,
    initialValues: assignmentClauseDetails!,
    validationSchema: validationSchema(assignmentMefFields),
    onSubmit: (fieldValues) => {
      if (proposalStatus !== "MEF Submitted") {
        const mapper = prepareAssignmentRequestBody(leadId, fieldValues);
        updateAssignmentDetails(
          {
            applicationId: assignmentClauseDetails.applicationId,
            reqBody: mapper
          },
          {
            onSuccess: (res: Record<string, any>) => {
              dispatch(setAssignmentClause({ ...fieldValues, applicationId: res?.data?.id }));
              dispatch(setStepDetails({ parentStepNo: parentStepNumber + 1, reviewStepNo: 1 }));
              window.scrollTo(0, 0);
            }
          }
        );
      }
    }
  });

  const handleFieldChange = (e: React.ChangeEvent<any>) => {
    if (formik.values.witnessSignature) {
      formik.setFieldValue("witnessSignature", "");
    }
    formik.handleChange(e);
  };

  const handleFieldSetValue = (field: string, value: any) => {
    if (formik.values.witnessSignature && field !== "witnessSignature") {
      formik.setFieldValue("witnessSignature", "");
    }
    formik.setFieldValue(field, value);
  };

  const { mutate: sendOtpMutation, isLoading: isSendOtpLoading } = useMutation(
    async () => {
      const email = formik.values.witnessEmail || "";
      const mobile = formik.values.witnessMobile || "";
      const response = await OtpService.sendOtpToEmailAndMobile(email, mobile, 6);
      return response;
    },
    {
      retry: false,
      onSuccess: (response: any) => {
        const mobile = formik.values.witnessMobile || "";
        dispatch(
          setOtpVerificationDetails({
            otpVerificationId: response?.data?.id,
            mobile
          })
        );
        setIsOtpModalOpen(true);
      }
    }
  );

  const { mutate: sendMobileOtpMutation, isLoading: isSendMobileOtpLoading } = useMutation(
    async (mobile: string) => {
      const email = formik.values.witnessEmail || "";
      return OtpService.sendOtpToEmailAndMobile(email, mobile, 6);
    },
    {
      retry: false,
      onSuccess: (response: any, mobile: string) => {
        formik.setFieldValue("witnessMobile", mobile);
        store.dispatch(
          setOtpVerificationDetails({
            otpVerificationId: response?.data?.id,
            mobile
          })
        );
        store.dispatch(
          SET_TOAST_ERROR({
            error: false,
            type: "success",
            message: "OTP sent successfully to email and mobile"
          })
        );
      },
      onError: () => {
        store.dispatch(
          SET_TOAST_ERROR({
            error: true,
            type: "error",
            message: "Failed to send OTP. Please try again."
          })
        );
      }
    }
  );

  const { mutate: resendOtpMutation, isLoading: isReSendOtpLoading } = useMutation(
    async () => {
      const email = formik.values.witnessEmail || "";
      const mobile = formik.values.witnessMobile || "";
      const response = await OtpService.sendOtpToEmailAndMobile(email, mobile, 6);
      return response;
    },
    {
      retry: false,
      onSuccess: (response: any) => {
        const mobile = formik.values.witnessMobile || "";
        dispatch(
          setOtpVerificationDetails({
            otpVerificationId: response?.data?.id,
            mobile
          })
        );
      }
    }
  );

  const { mutate: verifyOtpMutation, isLoading: isVerifyingOtp } = useMutation(
    ({ id, otp }: { id: string; otp: string }) =>
      OtpService.validateOtp({ id, otp: parseInt(otp, 10) }),
    {
      onSuccess: async () => {
        const timestamp = new Date().toISOString();
        formik.setFieldValue("witnessSignature", timestamp);
        setIsOtpModalOpen(false);
        await new Promise((resolve) => {
          setTimeout(resolve, 0);
        });
        if (proposalStatus !== "MEF Submitted") {
          const updatedValues = { ...formik.values, witnessSignature: timestamp };
          const mapper = prepareAssignmentRequestBody(leadId, updatedValues);
          updateAssignmentDetails(
            {
              applicationId: assignmentClauseDetails.applicationId,
              reqBody: mapper
            },
            {
              onSuccess: (res: Record<string, any>) => {
                dispatch(setAssignmentClause({ ...updatedValues, applicationId: res?.data?.id }));
                dispatch(setStepDetails({ parentStepNo: parentStepNumber + 1, reviewStepNo: 1 }));
                window.scrollTo(0, 0);
              }
            }
          );
        }
      },
      onError: () => {
        dispatch(setOtpVerificationDetails({ otpValue: "" }));
      }
    }
  );

  const handleResendOTP = () => {
    resendOtpMutation();
  };

  const handleSendMobileOTP = (phone: string) => {
    sendMobileOtpMutation(phone);
  };

  const handleVerifyOTP = ({ id: verificationId, otp }: { id: string; otp: string }) => {
    verifyOtpMutation({ id: verificationId, otp });
  };

  const handleVerifyByOTP = () => {
    if (!formik.isValid) {
      formik.handleSubmit();
      setModalOpen(true);
      return;
    }
    sendOtpMutation();
  };

  const handleBack = () => {
    dispatch(setStepDetails({ parentStepNo: parentStepNumber - 1 }));
    window.scrollTo(0, 0);
  };

  const handleNext = () => {
    if (!formik.isValid) {
      setModalOpen(true);
    }
    formik.handleSubmit();
  };
  const user = StorageService.getFromLocalStorage("user");
  const isNBGO = user?.role === roleIDs.nbgo;

  return (
    <>
      <CustomBackDrop
        open={isAssignmentSaving || isSendOtpLoading || isReSendOtpLoading || isVerifyingOtp}
      />
      <CustomModal modalOpen={modalOpen} setModalOpen={setModalOpen} />
      <CustomOTPMobileModal
        isModal={isOtpModalOpen}
        onResendClick={handleResendOTP}
        onVerify={handleVerifyOTP}
        setIsOtpModalOpen={setIsOtpModalOpen}
        modalHeading="Please enter the 6-digit OTP"
        timerMinutes={2}
        showCancelButton
        onCancel={() => setIsOtpModalOpen(false)}
        otpLength={6}
        onMobileSendClick={handleSendMobileOTP}
        isSendingMobileOtp={isSendMobileOtpLoading}
      />
      <div className="min-h-screen bg-whiteSmoke pt-8 pb-19.5">
        <div className="md:w-805/1000 mx-auto bg-white pt-8.5 pb-10 rounded-1.5xl">
          <div className="pr-10 pl-10.25">
            <DetailsHeading label="Assignment Notice" />
          </div>
          <div className="pl-10.25 bg-white pr-10 py-8 rounded-b-1.5xl">
            <AssignmentClause
              formik={formik}
              assignmentMefFields={assignmentMefFields}
              onFieldChange={handleFieldChange}
              onFieldSetValue={handleFieldSetValue}
            />
          </div>
          <div className="mt-10 flex justify-center">
            <Button
              variant="outlined"
              classes={{ outlined: "!rounded-lg" }}
              onClick={handleBack}
              className="!text-black !uppercase h-13 w-37.5 rounded-lg !mr-10"
            >
              Back
            </Button>

            {partnerData?.showOtpVerification && (
              <Button
                variant="contained"
                classes={{ contained: "!rounded-lg" }}
                onClick={handleVerifyByOTP}
                disabled={!!formik.values.witnessSignature || isNBGO}
                className="!uppercase h-13 w-44 rounded-lg !mr-10"
              >
                Verify By OTP
              </Button>
            )}

            <Button
              variant="contained"
              classes={{ contained: "!rounded-lg" }}
              onClick={handleNext}
              disabled={!formik.values.witnessSignature && !isNBGO}
              className="!uppercase h-13 w-37.5 rounded-lg"
            >
              NEXT
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default AssignmentClauseDetails;
